import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AnalyticsDashboard = ({ analyticsData: propAnalyticsData }) => {
  const [analyticsData, setAnalyticsData] = useState({
    currentListeners: 0,
    listeners: [],
    countries: {},
    platforms: {},
    lastUpdated: null
  });
  // Removed loading state - data updates smoothly without overlays
  const [error, setError] = useState(null);

  // Use passed analytics data if available, otherwise fetch independently
  useEffect(() => {
    if (propAnalyticsData) {
      setAnalyticsData(propAnalyticsData);
      // Smooth data updates - no loading state needed
      setError(null);
    } else {
      fetchAnalyticsData();
    }
  }, [propAnalyticsData]);

  const fetchAnalyticsData = async () => {
    try {
      // Smooth data updates without loading overlay
      setError(null);
      console.log('Fetching nginx analytics dashboard data...');
      
      const response = await axios.get('/api/current-listeners');
      setAnalyticsData(response.data);
      console.log('Analytics updated:', response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError('Failed to load analytics data');
    } finally {
      // Smooth data updates - no loading state needed
    }
  };

  useEffect(() => {
    // Only auto-update if we're not receiving prop data
    if (!propAnalyticsData) {
      const interval = setInterval(fetchAnalyticsData, 10000);
      return () => clearInterval(interval);
    }
  }, [propAnalyticsData]);

  const formatCountryStats = (countries) => {
    return Object.entries(countries)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10); // Top 10 countries
  };

  const formatPlatformStats = (platforms) => {
    return Object.entries(platforms)
      .filter(([,count]) => count > 0)
      .sort(([,a], [,b]) => b - a);
  };

  if (analyticsData.currentListeners === 0) {
    return (
      <div className="analytics-dashboard">
        <h3>Loading Real-Time Analytics...</h3>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      <div className="analytics-header">
        <h2>Real-Time Listener Analytics</h2>
        <div className="analytics-meta">
          <span className="listener-count">
            {analyticsData.currentListeners} Active Listeners
          </span>
          {analyticsData.lastUpdated && (
            <span className="last-updated">
              Updated: {new Date(analyticsData.lastUpdated).toLocaleString("en-IE", {
                timeZone: "Europe/Dublin",
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
              })}
            </span>
          )}
        </div>
      </div>

      <div className="analytics-grid">
        {/* Country Breakdown */}
        <div className="analytics-card">
          <h3>Countries</h3>
          {analyticsData.countries && Object.keys(analyticsData.countries).length > 0 ? (
            <div className="country-list">
              {formatCountryStats(analyticsData.countries).map(([country, count]) => (
                <div key={country} className="country-item">
                  <span className="country-name">{country}</span>
                  <span className="country-count">{count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No geographic data available</p>
          )}
        </div>

        {/* Platform Breakdown */}
        <div className="analytics-card">
          <h3>Platforms</h3>
          {analyticsData.platforms && Object.keys(analyticsData.platforms).length > 0 ? (
            <div className="platform-list">
              {formatPlatformStats(analyticsData.platforms).map(([platform, count]) => (
                <div key={platform} className="platform-item">
                  <span className="platform-name">
                    {platform === 'tunein' && 'TuneIn'}
                    {platform === 'radio_net' && 'Radio.net'}
                    {platform === 'website' && 'Website'}
                    {platform === 'mobile' && 'Mobile'}
                    {platform === 'desktop' && 'Desktop'}
                    {platform === 'unknown' && 'Unknown'}
                  </span>
                  <span className="platform-count">{count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No platform data available</p>
          )}
        </div>

        {/* Recent Listeners */}
        <div className="analytics-card full-width">
          <h3>Recent Real Listeners</h3>
          {analyticsData.listeners.length > 0 ? (
            <div className="recent-listeners">
              {analyticsData.listeners.slice(0, 10).map((listener, index) => (
                <div key={index} className="recent-listener">
                  <span className="listener-ip">{listener.ip}</span>
                  <span className="listener-location">
                    {listener.location?.city || 'Unknown'}, {listener.location?.country || 'Unknown'}
                  </span>
                  <span className="listener-stream">{listener.stream}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No recent listeners with real IPs</p>
          )}
        </div>
      </div>

      {error && (
        <div className="analytics-error">
          Error: {error}
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;